// src/pages/RetasTorneosPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Button, Center, Spinner,
  VStack, HStack, Avatar, Divider, useColorModeValue, useDisclosure,
  IconButton, Icon, useBreakpointValue, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Tag
} from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight, FaTrophy, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { useAuthCliente } from '../context/AuthClienteContext';
import PlayerProfileModal from '../components/PlayerProfileModal';
import RetaBracketModal from '../components/RetaBracketModal';

// --- Tarjeta para cada evento de Reta (sin cambios) ---
const RetaInfoCard = ({ reta, onClick }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const date = reta.createdAt?.toDate().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Box
      // ✅ Se elimina la altura fija para permitir que la tarjeta crezca si es necesario
      bg={cardBg}
      borderRadius="xl"
      boxShadow="lg"
      p={5}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
      cursor="pointer"
      onClick={onClick}
      // Aseguramos que todas las tarjetas en una fila tengan la misma altura
      display="flex"
      flexDirection="column"
    >
      <VStack w="full" h="full" spacing={4} textAlign="center" justifyContent="center" flex={1}>
        {/* ✅ Se elimina la propiedad 'noOfLines={2}' para mostrar el título completo */}
        <Heading size="md">{reta.name}</Heading>
        <Text fontSize="sm" color="gray.500">{date}</Text>
        <Divider />
        <Icon as={FaTrophy} w={10} h={10} color="yellow.400" />
        <Box>
          <Text fontSize="sm" color="gray.600">Campeón</Text>
          <Heading size="lg" color="brand.600">{reta.winnerInfo?.name || 'Indefinido'}</Heading>
        </Box>
        <Button 
        _hover={{ bg: 'green.600', color: 'white', cursor: 'pointer' }}
            size="sm" 
            variant="outline"
            colorScheme="green"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
        >
            Ver Resultados
        </Button>
      </VStack>
    </Box>
  );
};

// --- ✅ TABLA DE RANKING COMPLETA Y CORREGIDA ---
function FullRankingTable({ members, onViewProfile }) {
  return (
    <TableContainer bg="white" borderRadius="lg" boxShadow="md" borderWidth="1px" borderColor="gray.200">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>Jugador</Th>
            <Th>Categoría</Th>
            {/* Columnas añadidas */}
            <Th>Sexo</Th>
            <Th isNumeric>Edad</Th>
            <Th isNumeric>Puntos de Retas</Th>
            <Th textAlign="center">Cambio</Th>
          </Tr>
        </Thead>
        <Tbody>
          {members.map((member) => {
            // Lógica para calcular edad y sexo
            const birth = member.birthDate ? new Date(member.birthDate) : null;
            const age = birth ? Math.floor((Date.now() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A';
            const sexoLabel = member.sexo?.startsWith('f') ? 'Femenino' : (member.sexo?.startsWith('m') ? 'Masculino' : 'N/A');
            
            return (
              <Tr 
                key={member.id} 
                _hover={{ bg: 'green.600', color: 'white', cursor: 'pointer' }}
                onClick={() => onViewProfile(member)}
              >
                <Td fontWeight="bold">{member.rank}</Td>
                <Td>
                  <HStack>
                    <Avatar size="sm" name={member.name} src={member.photoURL} />
                    <Text>{member.name}</Text>
                  </HStack>
                </Td>
                <Td>{member.category || 'N/A'}</Td>
                {/* Celdas de datos añadidas */}
                <Td>{sexoLabel}</Td>
                <Td isNumeric>{age}</Td>
                <Td isNumeric fontWeight="bold" color="brand.600">{member.retasPoints || 0}</Td>
                <Td textAlign="center">
                  {member.change === 'up' && <Icon as={FaArrowUp} color="green.500" />}
                  {member.change === 'down' && <Icon as={FaArrowDown} color="red.500" />}
                  {member.change === 'same' && <Text color="gray.400">-</Text>}
                  {member.change === 'new' && <Tag size="sm" colorScheme="blue">Nuevo</Tag>}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export default function RetasTorneosPage() {
  const { currentUser } = useAuthCliente();
  const [retas, setRetas] = useState([]);
  const [loadingRetas, setLoadingRetas] = useState(true);

  const [retasRanking, setRetasRanking] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(true);
  const previousRankingRef = useRef([]);
  const [rankingPage, setRankingPage] = useState(0);
  const itemsPerRankingPage = 10;

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = useBreakpointValue({ base: 2, md: 3, lg: 3 }) || 3;
  
  const { isOpen: isBracketOpen, onOpen: onBracketOpen, onClose: onBracketClose } = useDisclosure();
  const [selectedReta, setSelectedReta] = useState(null);
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    if (!currentUser?.companyId) {
        setLoadingRetas(false);
        return;
    }
    const retasRef = collection(db, 'companies', currentUser.companyId, 'tournaments');
    const q = query(retasRef, where('type', '==', 'reta'), where('status', '==', 'completed'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRetas(data);
        setLoadingRetas(false);
    });
    return () => unsub();
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (!currentUser?.companyId) {
        setLoadingRanking(false);
        return;
    };
    setLoadingRanking(true);
    const playersRef = collection(db, 'companies', currentUser.companyId, 'end_customers');
    const q = query(playersRef);
    const unsub = onSnapshot(q, (snapshot) => {
        const newList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        newList.sort((a, b) => (b.retasPoints || 0) - (a.retasPoints || 0) || a.name.localeCompare(b.name));

        const rankingWithChanges = newList.map((currentMember, newIndex) => {
            const oldIndex = previousRankingRef.current.findIndex(oldMember => oldMember.id === currentMember.id);
            let change = 'same';
            if (oldIndex === -1) change = 'new';
            else if (newIndex < oldIndex) change = 'up';
            else if (newIndex > oldIndex) change = 'down';
            return { ...currentMember, change, rank: newIndex + 1 };
        });

        setRetasRanking(rankingWithChanges);
        previousRankingRef.current = newList;
        setLoadingRanking(false);
    });
    return () => unsub();
  }, [currentUser?.companyId]);

  const handleRetaClick = (reta) => {
    setSelectedReta(reta);
    onBracketOpen();
  };

  const handleViewProfile = (player) => {
    setSelectedPlayer(player);
    onProfileOpen();
  };

  const totalPages = Math.ceil(retas.length / itemsPerPage);
  const paginatedRetas = retas.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const totalRankingPages = Math.ceil(retasRanking.length / itemsPerRankingPage);
  const paginatedRanking = retasRanking.slice(rankingPage * itemsPerRankingPage, (rankingPage + 1) * itemsPerRankingPage);

  return (
    <>
      <RetaBracketModal isOpen={isBracketOpen} onClose={onBracketClose} tournament={selectedReta} />
      <PlayerProfileModal isOpen={isProfileOpen} onClose={onProfileClose} member={selectedPlayer} />

      <Box>
        <VStack spacing={12} align="stretch">
          <Box>
            <Heading mb={6}>Resultados de Retas</Heading>
            {loadingRetas ? (
              <Center h="200px"><Spinner size="xl" /></Center>
            ) : retas.length > 0 ? (
              <VStack align="stretch" spacing={6}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {paginatedRetas.map(reta => <RetaInfoCard key={reta.id} reta={reta} onClick={() => handleRetaClick(reta)} />)}
                </SimpleGrid>
                {totalPages > 1 && (
                  <HStack justify="center" mt={4}>
                    <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setCurrentPage(p => p - 1)} isDisabled={currentPage === 0} />
                    <Text fontSize="sm" color="gray.600">{currentPage + 1} / {totalPages}</Text>
                    <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setCurrentPage(p => p + 1)} isDisabled={currentPage >= totalPages - 1} />
                  </HStack>
                )}
              </VStack>
            ) : (
              <Center p={8} bg="gray.50" borderRadius="md">
                <Text>Aún no se han registrado resultados de retas.</Text>
              </Center>
            )}
          </Box>

          <Divider />

          <Box>
            <Heading size="lg" mb={4}>Ranking Completo de Retas</Heading>
            {loadingRanking ? (
              <Center h="200px"><Spinner size="xl" /></Center>
            ) : retasRanking.length > 0 ? (
              <VStack align="stretch" spacing={4}>
                <FullRankingTable members={paginatedRanking} onViewProfile={handleViewProfile} />
                {totalRankingPages > 1 && (
                  <HStack justify="center">
                    <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setRankingPage(p => p - 1)} isDisabled={rankingPage === 0} />
                    <Text fontSize="sm" color="gray.600">{rankingPage + 1} / {totalRankingPages}</Text>
                    <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setRankingPage(p => p + 1)} isDisabled={rankingPage >= totalRankingPages - 1} />
                  </HStack>
                )}
              </VStack>
            ) : (
               <Center p={8} bg="gray.50" borderRadius="md">
                  <Text>No hay datos de ranking de retas disponibles.</Text>
              </Center>
            )}
          </Box>
        </VStack>
      </Box>
    </>
  );
}